/**
 * Group elements of the array by keys returned by the supplied callback.
 * @param collection - Array of items
 * @param groupingFn - to be called with each item, its index, and the original array. Return a string to group by.
 */
export function _groupBy<T, R extends string>(
    collection: T[],
    groupingFn: (item: T, index: number, collection: T[]) => R,
): { [key in R]: T[] };

/**
 * Group elements of the dictionary by keys returned by the supplied callback.
 * @param collection - Dictionary of items
 * @param groupingFn - to be called with each item, its index, and the original dictionary. Return a string to group by.
 */
export function _groupBy<T extends object, R extends string>(
    collection: T,
    groupingFn: (item: T[keyof T], index: string, collection: T) => R,
): { [key in R]: T[keyof T][] };

/**
 * Group elements of the array by numbers returned by the supplied callback.
 * @param collection - Array of items
 * @param groupingFn - to be called with each item, its index, and the original array. Return a number to group by.
 */
export function _groupBy<T, R extends number>(
    collection: T[],
    groupingFn: (item: T, index: number, collection: T[]) => R,
): { [key in R]: T[] };

/**
 * Group elements of the dictionary by numbers returned by the supplied callback.
 * @param collection - Dictionary of items
 * @param groupingFn - to be called with each item, its index, and the original dictionary. Return a number to group by.
 */
export function _groupBy<T extends object, R extends number>(
    collection: T,
    groupingFn: (item: T[keyof T], index: string, collection: T) => R,
): { [key in R]: T[keyof T][] };

/**
 * Group elements of the array by the value accessed by the given key.
 * @param collection - Array of items
 * @param groupBy - A key of the supplied items, items will be grouped by the value accessed by this key.
 */
export function _groupBy<T, R extends keyof T>(
    collection: T[],
    groupBy: R,
): T[R] extends number | string ? { [key in T[R]]: T[] } : never;

/**
 * Group elements of the dictionary by the value accessed by the given key.
 * @param collection - Dictionary of items
 * @param groupBy - A key of the supplied items, items will be grouped by the value accessed by this key.
 */
export function _groupBy<T, R extends keyof T>(
    collection: Lookup<T>,
    groupBy: R,
): T[R] extends number | string ? { [key in T[R]]: T[] } : never;

/**
 * Null | undefined collection will return an empty dictionary.
 */
export function _groupBy(collection: null | undefined, groupBy: any): object;

/**
 * Override implementation.
 */
export function _groupBy(collection: Array<any> | Lookup<any>, groupBy: any): any {
	if (!collection || !groupBy) return {}
	const result: any = {}

	for (const key in collection) {
		let groupKey
		if (typeof groupBy === 'function') {
			groupKey = groupBy(collection[key], Array.isArray(collection) ? +key : key, collection)
		} else {
			groupKey = collection[key][groupBy]
		}
		const group = result[groupKey] = result[groupKey] || []
		group.push(collection[key])
	}
	return result
}